/**
 * CompareProperties - Side-by-side property comparison
 * Allows users to select and compare multiple properties
 * Features: Share link, PDF export, Price History Charts
 */
import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea, ScrollBar } from './ui/scroll-area';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { 
  X, Plus, Check, AlertCircle, 
  Bed, Bath, Square, MapPin, Home, Car,
  Zap, Droplets, Wifi, Shield, Trees, Building2,
  ChevronDown, ChevronUp, Scale, Trash2,
  Share2, Download, Copy, Link, Loader2, FileText, Bookmark, Bell, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import { ComparisonPriceHistory } from './PriceHistoryChart';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Feature icons mapping
const FEATURE_ICONS = {
  parking: Car,
  garden: Trees,
  lift: Building2,
  gym: Zap,
  pool: Droplets,
  wifi: Wifi,
  '24hr_security': Shield,
  generator: Zap,
  water_supply: Droplets,
  balcony: Building2,
  ensuite: Bath,
  bills_included: Check,
  pets_allowed: Check,
  furnished: Home,
  unfurnished: Home,
};

// Comparison row component
const ComparisonRow = ({ label, icon: Icon, values, type = 'text', highlight = false }) => {
  const getBestValue = () => {
    if (type === 'price') {
      return Math.min(...values.filter(v => v !== null && v !== undefined));
    }
    if (type === 'number') {
      return Math.max(...values.filter(v => v !== null && v !== undefined));
    }
    return null;
  };
  
  const bestValue = getBestValue();
  
  return (
    <div className={`grid border-b ${highlight ? 'bg-blue-50' : ''}`} 
         style={{ gridTemplateColumns: `200px repeat(${values.length}, 1fr)` }}>
      <div className="p-3 border-r bg-gray-50 flex items-center gap-2 font-medium text-gray-700">
        {Icon && <Icon className="w-4 h-4 text-gray-500" />}
        {label}
      </div>
      {values.map((value, idx) => {
        const isEqual = value === bestValue && type !== 'text';
        const isBest = value === bestValue && type === 'price';
        
        return (
          <div 
            key={idx} 
            className={`p-3 border-r text-center ${
              isBest ? 'bg-green-50 text-green-700 font-semibold' : ''
            } ${isEqual && type === 'number' ? 'bg-blue-50 text-blue-700 font-semibold' : ''}`}
          >
            {value !== null && value !== undefined ? (
              type === 'price' ? (
                <span>{value.toLocaleString()}/mo</span>
              ) : type === 'boolean' ? (
                value ? <Check className="w-5 h-5 text-green-500 mx-auto" /> : 
                       <X className="w-5 h-5 text-gray-300 mx-auto" />
              ) : type === 'features' ? (
                <div className="flex flex-wrap gap-1 justify-center">
                  {(value || []).slice(0, 4).map((f, i) => {
                    const FeatureIcon = FEATURE_ICONS[f] || Check;
                    return (
                      <Badge key={i} variant="secondary" className="text-xs">
                        <FeatureIcon className="w-3 h-3 mr-1" />
                        {f.replace('_', ' ')}
                      </Badge>
                    );
                  })}
                  {(value || []).length > 4 && (
                    <Badge variant="secondary" className="text-xs">
                      +{value.length - 4} more
                    </Badge>
                  )}
                </div>
              ) : (
                value
              )
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Main compare modal component
export default function CompareProperties({ 
  isOpen, 
  onClose, 
  selectedIds = [],
  onRemove,
  currency = 'Rs.'
}) {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    details: true,
    features: true,
    location: true
  });
  const [shareUrl, setShareUrl] = useState('');
  const [isSharing, setIsSharing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveEmail, setSaveEmail] = useState('');
  const [saveTitle, setSaveTitle] = useState('');
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [alertEmail, setAlertEmail] = useState('');
  const [alertTitle, setAlertTitle] = useState('');
  const [targetPrice, setTargetPrice] = useState('');
  const [alertType, setAlertType] = useState('any');
  const [isCreatingAlert, setIsCreatingAlert] = useState(false);
  const comparisonRef = useRef(null);
  
  // Fetch property details
  useEffect(() => {
    if (!isOpen || selectedIds.length === 0) {
      setProperties([]);
      setShareUrl('');
      return;
    }
    
    const fetchProperties = async () => {
      setLoading(true);
      try {
        const results = await Promise.all(
          selectedIds.map(async (id) => {
            const res = await fetch(`${API_URL}/api/listings/${id}`);
            if (res.ok) return res.json();
            return null;
          })
        );
        setProperties(results.filter(Boolean));
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProperties();
  }, [isOpen, selectedIds]);
  
  // Generate shareable link
  const handleShare = async () => {
    setIsSharing(true);
    try {
      const response = await fetch(`${API_URL}/api/compare/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          listing_ids: selectedIds,
          title: `Compare ${properties.length} Properties`
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        const fullUrl = `${window.location.origin}${data.share_url}`;
        setShareUrl(fullUrl);
        
        // Copy to clipboard
        await navigator.clipboard.writeText(fullUrl);
        toast.success('Share link copied to clipboard!');
      } else {
        throw new Error('Failed to create share link');
      }
    } catch (error) {
      toast.error('Failed to create share link');
      console.error(error);
    } finally {
      setIsSharing(false);
    }
  };
  
  // Export to PDF
  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      // Dynamically import jspdf and html2canvas
      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import('jspdf'),
        import('html2canvas')
      ]);
      
      const pdf = new jsPDF('l', 'mm', 'a4'); // Landscape for comparison
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      // Title
      pdf.setFontSize(20);
      pdf.setTextColor(59, 130, 246);
      pdf.text('Property Comparison', 14, 20);
      
      pdf.setFontSize(10);
      pdf.setTextColor(107, 114, 128);
      pdf.text(`Generated on ${new Date().toLocaleDateString()}`, 14, 28);
      
      let yPosition = 40;
      const colWidth = (pageWidth - 60) / properties.length;
      
      // Property headers
      properties.forEach((prop, index) => {
        const xPos = 30 + (index * colWidth);
        
        pdf.setFontSize(12);
        pdf.setTextColor(31, 41, 55);
        pdf.text(prop.title?.substring(0, 30) || 'Property', xPos, yPosition, { maxWidth: colWidth - 10 });
        
        pdf.setFontSize(14);
        pdf.setTextColor(59, 130, 246);
        pdf.text(`${currency}${prop.price?.toLocaleString() || 0}/mo`, xPos, yPosition + 8);
        
        pdf.setFontSize(10);
        pdf.setTextColor(107, 114, 128);
        pdf.text(prop.location || '', xPos, yPosition + 14, { maxWidth: colWidth - 10 });
      });
      
      yPosition += 25;
      
      // Comparison rows
      const rows = [
        { label: 'Bedrooms', key: 'bedrooms', format: v => v === 0 ? 'Studio' : v || '-' },
        { label: 'Bathrooms', key: 'bathrooms', format: v => v || '-' },
        { label: 'Property Type', key: 'property_type', format: v => v?.replace('_', ' ') || '-' },
        { label: 'Furnishing', key: 'furnishing', format: v => v?.replace('_', ' ') || '-' },
        { label: 'Condition', key: 'condition', format: v => v || '-' },
      ];
      
      // Draw table
      pdf.setDrawColor(229, 231, 235);
      pdf.setLineWidth(0.5);
      
      rows.forEach((row, rowIndex) => {
        const rowY = yPosition + (rowIndex * 12);
        
        // Row label
        pdf.setFontSize(10);
        pdf.setTextColor(107, 114, 128);
        pdf.text(row.label, 14, rowY);
        
        // Values
        properties.forEach((prop, colIndex) => {
          const xPos = 30 + (colIndex * colWidth);
          pdf.setTextColor(31, 41, 55);
          pdf.text(String(row.format(prop[row.key])), xPos, rowY);
        });
        
        // Draw line
        pdf.line(14, rowY + 3, pageWidth - 14, rowY + 3);
      });
      
      yPosition += rows.length * 12 + 15;
      
      // Features section
      pdf.setFontSize(12);
      pdf.setTextColor(31, 41, 55);
      pdf.text('Features', 14, yPosition);
      yPosition += 8;
      
      properties.forEach((prop, index) => {
        const xPos = 30 + (index * colWidth);
        const features = prop.features || [];
        
        pdf.setFontSize(9);
        pdf.setTextColor(107, 114, 128);
        features.slice(0, 6).forEach((feat, i) => {
          pdf.text(`• ${feat.replace('_', ' ')}`, xPos, yPosition + (i * 5));
        });
      });
      
      // Footer
      pdf.setFontSize(8);
      pdf.setTextColor(156, 163, 175);
      pdf.text('Generated by Property Marketplace', 14, pageHeight - 10);
      
      // Save PDF
      pdf.save(`property-comparison-${Date.now()}.pdf`);
      toast.success('PDF exported successfully!');
      
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    } finally {
      setIsExporting(false);
    }
  };
  
  // Save comparison to user's account
  const handleSave = async () => {
    if (!saveEmail.trim() || !saveEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/compare/saved?user_email=${encodeURIComponent(saveEmail)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          listing_ids: selectedIds,
          title: saveTitle.trim() || `Compare ${properties.length} Properties`
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Comparison saved!');
        setShowSaveDialog(false);
        setSaveEmail('');
        setSaveTitle('');
        // Store email for convenience
        localStorage.setItem('savedComparisonsEmail', saveEmail);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save comparison');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save comparison');
    } finally {
      setIsSaving(false);
    }
  };
  
  const openSaveDialog = () => {
    // Pre-fill email if stored
    const storedEmail = localStorage.getItem('savedComparisonsEmail');
    if (storedEmail) {
      setSaveEmail(storedEmail);
    }
    setSaveTitle(`Compare ${properties.length} Properties`);
    setShowSaveDialog(true);
  };
  
  // Create price alert
  const handleCreateAlert = async () => {
    if (!alertEmail.trim() || !alertEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    const price = parseFloat(targetPrice);
    if (isNaN(price) || price <= 0) {
      toast.error('Please enter a valid target price');
      return;
    }
    
    setIsCreatingAlert(true);
    try {
      const response = await fetch(`${API_URL}/api/compare/price-alert?user_email=${encodeURIComponent(alertEmail)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          listing_ids: selectedIds,
          target_price: price,
          alert_type: alertType,
          title: alertTitle.trim() || `Price Alert for ${properties.length} Properties`
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Price alert created! We\'ll email you when prices drop.');
        setShowAlertDialog(false);
        setAlertEmail('');
        setAlertTitle('');
        setTargetPrice('');
        setAlertType('any');
        // Store email for convenience
        localStorage.setItem('savedComparisonsEmail', alertEmail);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create price alert');
      }
    } catch (error) {
      console.error('Alert error:', error);
      toast.error('Failed to create price alert');
    } finally {
      setIsCreatingAlert(false);
    }
  };
  
  const openAlertDialog = () => {
    // Pre-fill email if stored
    const storedEmail = localStorage.getItem('savedComparisonsEmail');
    if (storedEmail) {
      setAlertEmail(storedEmail);
    }
    // Calculate average price for suggestion
    const prices = properties.map(p => p.price || 0).filter(p => p > 0);
    if (prices.length > 0) {
      const avgPrice = Math.round(prices.reduce((a, b) => a + b, 0) / prices.length);
      setTargetPrice(String(Math.round(avgPrice * 0.9))); // Suggest 10% below average
    }
    setAlertTitle(`Price Alert for ${properties.length} Properties`);
    setShowAlertDialog(true);
  };
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  // Copy share URL to clipboard
  const copyShareUrl = async () => {
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied!');
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] p-0 overflow-hidden w-[95vw] sm:w-auto">
        <DialogHeader className="p-3 sm:p-4 border-b bg-gradient-to-r from-blue-600 to-indigo-600">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Scale className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              <div>
                <DialogTitle className="text-white text-lg sm:text-xl">Compare Properties</DialogTitle>
                <DialogDescription className="text-blue-100 text-sm">
                  {properties.length} properties selected
                </DialogDescription>
              </div>
            </div>
            
            {/* Share, Save, Alert and Export buttons */}
            <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleShare}
                disabled={isSharing || properties.length < 2}
                className="bg-white/20 hover:bg-white/30 text-white border-0 h-8 px-2 sm:px-3"
                data-testid="share-comparison-btn"
              >
                {isSharing ? (
                  <Loader2 className="w-4 h-4 sm:mr-1 animate-spin" />
                ) : (
                  <Share2 className="w-4 h-4 sm:mr-1" />
                )}
                <span className="hidden sm:inline">Share</span>
              </Button>
              
              <Button
                variant="secondary"
                size="sm"
                onClick={openSaveDialog}
                disabled={properties.length < 2}
                className="bg-white/20 hover:bg-white/30 text-white border-0 h-8 px-2 sm:px-3"
                data-testid="save-comparison-btn"
              >
                <Bookmark className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Save</span>
              </Button>
              
              <Button
                variant="secondary"
                size="sm"
                onClick={openAlertDialog}
                disabled={properties.length < 2}
                className="bg-orange-500/80 hover:bg-orange-500 text-white border-0 h-8 px-2 sm:px-3"
                data-testid="price-alert-btn"
              >
                <Bell className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Alert</span>
              </Button>
              
              <Button
                variant="secondary"
                size="sm"
                onClick={handleExportPDF}
                disabled={isExporting || properties.length < 2}
                className="bg-white/20 hover:bg-white/30 text-white border-0 h-8 px-2 sm:px-3"
                data-testid="export-pdf-btn"
              >
                {isExporting ? (
                  <Loader2 className="w-4 h-4 sm:mr-1 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4 sm:mr-1" />
                )}
                <span className="hidden sm:inline">PDF</span>
              </Button>
            </div>
          </div>
          
          {/* Share URL display */}
          {shareUrl && (
            <div className="mt-3 flex items-center gap-2 bg-white/10 rounded-lg p-2">
              <Link className="w-4 h-4 text-white/70 flex-shrink-0" />
              <Input
                value={shareUrl}
                readOnly
                className="h-8 bg-transparent border-0 text-white text-xs sm:text-sm focus-visible:ring-0"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={copyShareUrl}
                className="text-white hover:bg-white/20 h-8"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          )}
        </DialogHeader>
        
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-500">Loading properties...</p>
          </div>
        ) : properties.length === 0 ? (
          <div className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No properties selected for comparison</p>
          </div>
        ) : (
          <ScrollArea className="max-h-[calc(90vh-100px)]" ref={comparisonRef}>
            <div className="min-w-max overflow-x-auto">
              {/* Property Headers */}
              <div className="grid sticky top-0 z-10 bg-white border-b shadow-sm"
                   style={{ gridTemplateColumns: `150px repeat(${properties.length}, minmax(150px, 1fr))` }}>
                <div className="p-2 sm:p-3 border-r bg-gray-100 font-semibold text-gray-700 text-sm">
                  Details
                </div>
                {properties.map((prop, idx) => (
                  <div key={prop.id || idx} className="p-2 sm:p-3 border-r">
                    <div className="relative">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute -top-1 -right-1 h-6 w-6 text-gray-400 hover:text-red-500"
                        onClick={() => onRemove(prop.id)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                      <img 
                        src={prop.images?.[0] || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=200'}
                        alt={prop.title}
                        className="w-full h-24 object-cover rounded-lg mb-2"
                      />
                      <h4 className="font-semibold text-sm line-clamp-2 pr-6">{prop.title}</h4>
                      <p className="text-blue-600 font-bold text-lg mt-1">
                        {currency}{prop.price?.toLocaleString()}<span className="text-xs font-normal text-gray-500">/mo</span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Basic Info Section */}
              <div className="border-b">
                <button 
                  className="w-full p-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100"
                  onClick={() => toggleSection('basic')}
                >
                  <span className="font-semibold text-gray-700">Basic Information</span>
                  {expandedSections.basic ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {expandedSections.basic && (
                  <>
                    <ComparisonRow 
                      label="Price" 
                      icon={null}
                      values={properties.map(p => `${currency}${p.price?.toLocaleString()}`)} 
                      type="text"
                      highlight
                    />
                    <ComparisonRow 
                      label="Bedrooms" 
                      icon={Bed}
                      values={properties.map(p => p.bedrooms === 0 ? 'Studio' : p.bedrooms)} 
                      type="number"
                    />
                    <ComparisonRow 
                      label="Bathrooms" 
                      icon={Bath}
                      values={properties.map(p => p.bathrooms)} 
                      type="number"
                    />
                    <ComparisonRow 
                      label="Property Type" 
                      icon={Home}
                      values={properties.map(p => p.property_type?.replace('_', ' '))} 
                    />
                  </>
                )}
              </div>
              
              {/* Location Section */}
              <div className="border-b">
                <button 
                  className="w-full p-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100"
                  onClick={() => toggleSection('location')}
                >
                  <span className="font-semibold text-gray-700">Location</span>
                  {expandedSections.location ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {expandedSections.location && (
                  <>
                    <ComparisonRow 
                      label="Location" 
                      icon={MapPin}
                      values={properties.map(p => p.location)} 
                    />
                    <ComparisonRow 
                      label="Area/District" 
                      icon={Building2}
                      values={properties.map(p => p.area || p.district || '-')} 
                    />
                  </>
                )}
              </div>
              
              {/* Details Section */}
              <div className="border-b">
                <button 
                  className="w-full p-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100"
                  onClick={() => toggleSection('details')}
                >
                  <span className="font-semibold text-gray-700">Property Details</span>
                  {expandedSections.details ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {expandedSections.details && (
                  <>
                    <ComparisonRow 
                      label="Furnishing" 
                      icon={Home}
                      values={properties.map(p => p.furnishing?.replace('_', ' '))} 
                    />
                    <ComparisonRow 
                      label="Condition" 
                      icon={Check}
                      values={properties.map(p => p.condition)} 
                    />
                    <ComparisonRow 
                      label="Size (sqft)" 
                      icon={Square}
                      values={properties.map(p => p.sqft || p.size)} 
                      type="number"
                    />
                  </>
                )}
              </div>
              
              {/* Features Section */}
              <div className="border-b">
                <button 
                  className="w-full p-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100"
                  onClick={() => toggleSection('features')}
                >
                  <span className="font-semibold text-gray-700">Features & Amenities</span>
                  {expandedSections.features ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {expandedSections.features && (
                  <>
                    <ComparisonRow 
                      label="Parking" 
                      icon={Car}
                      values={properties.map(p => p.features?.includes('parking'))} 
                      type="boolean"
                    />
                    <ComparisonRow 
                      label="Garden" 
                      icon={Trees}
                      values={properties.map(p => p.features?.includes('garden'))} 
                      type="boolean"
                    />
                    <ComparisonRow 
                      label="Lift/Elevator" 
                      icon={Building2}
                      values={properties.map(p => p.features?.includes('lift'))} 
                      type="boolean"
                    />
                    <ComparisonRow 
                      label="24hr Security" 
                      icon={Shield}
                      values={properties.map(p => p.features?.includes('24hr_security'))} 
                      type="boolean"
                    />
                    <ComparisonRow 
                      label="Generator" 
                      icon={Zap}
                      values={properties.map(p => p.features?.includes('generator'))} 
                      type="boolean"
                    />
                    <ComparisonRow 
                      label="All Features" 
                      icon={null}
                      values={properties.map(p => p.features)} 
                      type="features"
                    />
                  </>
                )}
              </div>
              
              {/* Price History Chart */}
              {properties.length >= 2 && (
                <div className="p-4 border-t">
                  <ComparisonPriceHistory 
                    listingIds={selectedIds}
                    titles={properties.map(p => p.title)}
                  />
                </div>
              )}
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        )}
        
        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
          <p className="text-sm text-gray-500">
            Tip: Green highlights indicate the best value for price
          </p>
          <Button onClick={onClose}>
            Close Comparison
          </Button>
        </div>
      </DialogContent>
      
      {/* Save Comparison Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bookmark className="w-5 h-5 text-blue-600" />
              Save Comparison
            </DialogTitle>
            <DialogDescription>
              Save this comparison to access it later from any device
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Title</label>
              <Input
                placeholder="My Comparison"
                value={saveTitle}
                onChange={(e) => setSaveTitle(e.target.value)}
                data-testid="save-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Email *</label>
              <Input
                type="email"
                placeholder="your@email.com"
                value={saveEmail}
                onChange={(e) => setSaveEmail(e.target.value)}
                required
                data-testid="save-email-input"
              />
              <p className="text-xs text-gray-500">
                Used to retrieve your saved comparisons later
              </p>
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={isSaving || !saveEmail.trim()}
              data-testid="confirm-save-btn"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Bookmark className="w-4 h-4 mr-1" />
              )}
              Save Comparison
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Price Alert Dialog */}
      <Dialog open={showAlertDialog} onOpenChange={setShowAlertDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-orange-500" />
              Set Price Alert
            </DialogTitle>
            <DialogDescription>
              Get notified when property prices drop to your target
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Current prices summary */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700 mb-2">Current Prices</p>
              <div className="space-y-1">
                {properties.slice(0, 3).map((prop, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-gray-600 truncate max-w-[180px]">{prop.title}</span>
                    <span className="font-medium text-blue-600">{currency}{(prop.price || 0).toLocaleString()}/mo</span>
                  </div>
                ))}
                {properties.length > 3 && (
                  <p className="text-xs text-gray-500">+{properties.length - 3} more properties</p>
                )}
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Alert Name</label>
              <Input
                placeholder="Price Drop Alert"
                value={alertTitle}
                onChange={(e) => setAlertTitle(e.target.value)}
                data-testid="alert-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Target Price ({currency}/month) *</label>
              <Input
                type="number"
                placeholder="Enter your target price"
                value={targetPrice}
                onChange={(e) => setTargetPrice(e.target.value)}
                required
                data-testid="alert-price-input"
              />
              <p className="text-xs text-gray-500">
                You'll be notified when prices drop to or below this amount
              </p>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Alert When</label>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  type="button"
                  variant={alertType === 'any' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setAlertType('any')}
                  className="h-auto py-2"
                  data-testid="alert-type-any"
                >
                  <div className="text-center">
                    <p className="font-medium text-xs">Any</p>
                    <p className="text-[10px] opacity-70">Any drops</p>
                  </div>
                </Button>
                <Button
                  type="button"
                  variant={alertType === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setAlertType('all')}
                  className="h-auto py-2"
                  data-testid="alert-type-all"
                >
                  <div className="text-center">
                    <p className="font-medium text-xs">All</p>
                    <p className="text-[10px] opacity-70">All drop</p>
                  </div>
                </Button>
                <Button
                  type="button"
                  variant={alertType === 'average' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setAlertType('average')}
                  className="h-auto py-2"
                  data-testid="alert-type-average"
                >
                  <div className="text-center">
                    <p className="font-medium text-xs">Average</p>
                    <p className="text-[10px] opacity-70">Avg drops</p>
                  </div>
                </Button>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Email for Notifications *</label>
              <Input
                type="email"
                placeholder="your@email.com"
                value={alertEmail}
                onChange={(e) => setAlertEmail(e.target.value)}
                required
                data-testid="alert-email-input"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowAlertDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateAlert} 
              disabled={isCreatingAlert || !alertEmail.trim() || !targetPrice}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="confirm-alert-btn"
            >
              {isCreatingAlert ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Bell className="w-4 h-4 mr-1" />
              )}
              Create Alert
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

// Compare button component for listing cards
export function CompareButton({ 
  listingId, 
  isSelected, 
  onToggle,
  disabled = false 
}) {
  return (
    <Button
      variant={isSelected ? "default" : "outline"}
      size="sm"
      className={`h-7 text-xs ${isSelected ? 'bg-blue-600' : ''}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle(listingId);
      }}
      disabled={disabled}
      data-testid={`compare-btn-${listingId}`}
    >
      {isSelected ? (
        <>
          <Check className="w-3 h-3 mr-1" />
          Selected
        </>
      ) : (
        <>
          <Plus className="w-3 h-3 mr-1" />
          Compare
        </>
      )}
    </Button>
  );
}

// Floating compare bar component
export function CompareBar({ 
  selectedCount, 
  maxCount = 4,
  onCompare, 
  onClear 
}) {
  if (selectedCount === 0) return null;
  
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom">
      <Card className="flex items-center gap-4 px-4 py-3 shadow-xl bg-white border-blue-200">
        <div className="flex items-center gap-2">
          <Scale className="w-5 h-5 text-blue-600" />
          <span className="font-medium">
            {selectedCount} of {maxCount} selected
          </span>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onClear}
            className="text-gray-600"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Clear
          </Button>
          <Button
            size="sm"
            onClick={onCompare}
            disabled={selectedCount < 2}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Scale className="w-4 h-4 mr-1" />
            Compare ({selectedCount})
          </Button>
        </div>
      </Card>
    </div>
  );
}
